import { initActionTypes } from '@makemydeal/dr-shared-store';
import type { Reducer } from 'redux';
import { Actions } from '../actionTypes/actionTypes';
import type { DealerState } from './types';

/**
 * Reducer file name is confusing
 * need to change file name to dealer related as this reducer creates a dealer object in state tree
 *
 * NOTE (US2031952): the SCI dealer-details concerns (edition, dealer identity, address,
 * error details) are being consolidated onto the single top-level `state.dealer` slice. This
 * reducer now owns those cases in a flattened shape (dealerSettings/ids live
 * directly on `state.dealer`) in addition to the trade-in valuation settings it already held.
 */

export const dealerReducer: Reducer<DealerState> = (state: DealerState = {}, action: any) => {
    const { type } = action;

    switch (type) {
        case Actions.GET_DETAILS_SUCCESS: {
            // dealer settings are in action.payload.dealerSettings
            const dealerSettings = action.payload?.dealerSettings;
            if (dealerSettings) {
                // On success we preserve commonOrgId since some production dealers do not return it.
                const commonOrgId = state.commonOrgId;
                return {
                    ...state,
                    // Flatten the dealer-details API response onto state.dealer (identity, theme, address...).
                    ...action.payload,
                    aiSearchSettings: action.payload.aiSearchSettings ?? state.aiSearchSettings,
                    ...dealerSettings,
                    // Deep-merge dealerSettings so the edition + the trade-in valuation defaults (set at
                    // INIT_PENCIL_SUCCESS / initial state) are not erased by a dealer-details response that
                    // omits them — which would flip isShowroom=false and cause a spurious INVALID → /deal-setup redirect.
                    dealerSettings: {
                        ...state.dealerSettings,
                        ...dealerSettings
                    },
                    isTestDealer: action.payload.isTestDealer ?? state.isTestDealer,
                    commonOrgId
                };
            }
            return state;
        }

        case initActionTypes.INIT_PENCIL_SUCCESS: {
            // The bootstrap payload nests dealer config under dealerDetails.dealerSettings; the
            // inner `.dealerSettings` object carries the edition + the trade-in valuation flags.
            const dealerSettingsWrapper = action.payload?.dealerDetails?.dealerSettings;
            const dealerSettings = dealerSettingsWrapper?.dealerSettings;
            if (dealerSettings) {
                return {
                    ...state,
                    // Flatten the wrapper (aiSearchSettings, identity) and deep-merge the inner
                    // dealerSettings (edition + trade-in valuation flags) into state.dealer.dealerSettings.
                    ...dealerSettingsWrapper,
                    ...dealerSettings,
                    dealerSettings: {
                        ...state.dealerSettings,
                        ...dealerSettings
                    },
                    isTestDealer: dealerSettingsWrapper.isTestDealer ?? state.isTestDealer,
                    aiSearchSettings: dealerSettingsWrapper.aiSearchSettings ?? state.aiSearchSettings
                };
            }
            return state;
        }

        case Actions.DEALER_DETAILS: {
            return {
                ...state,
                dealerMMDId: action.payload.dealerMMDId
            };
        }

        case Actions.UPDATE_DEALER_DETAILS: {
            return {
                ...state,
                ...action.payload.data.dealerSettings,
                ...action.payload.data.aiSearchSettings,
                dealerMMDId: action.payload.data.dealerMMDId,
                commonOrgId: action.payload.data.commonOrgId,
                dnaAccountId: action.payload.data.dnaAccountId,
                dealerName: action.payload.data.dealerName,
                dealershipId: action.payload.data.dealershipId,
                theme: action.payload.data.theme,
                address: action.payload.data.address
            };
        }

        default: {
            return state;
        }
    }
};
